import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Moon, Sun, LogOut, Trash2, ChevronRight, Leaf, ShoppingBag, CalendarCheck, Salad, UserCircle, Loader2, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Profile() {
  const navigate = useNavigate();
  const { user, isGuest, dark, toggleDark, logout, setUser } = useApp();
  const [deleting, setDeleting] = useState(false);

  const initials = (user?.name || "G U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/auth");
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      setUser(null);
      toast.success("Account deleted");
      navigate("/auth");
    } catch {
      toast.error("Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="animate-fade-in pb-4">
      {/* Header */}
      <div className="px-5 pt-5 pb-6 bg-emerald-800 dark:bg-emerald-900 rounded-b-3xl">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="avatar" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-emerald-400/40" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-emerald-600 text-white flex items-center justify-center text-xl font-bold">{initials}</div>
          )}
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-white font-head" data-testid="profile-name">{user?.name || (isGuest ? "Guest User" : "Welcome")}</h1>
            <p className="text-sm text-emerald-200/80">{user?.email || "Not signed in"}</p>
            {user?.auth_provider === "google" && (
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-200 bg-emerald-700/50 px-2 py-0.5 rounded-full">
                <ShieldCheck className="w-3 h-3" /> Google Account
              </span>
            )}
          </div>
        </div>
        {(isGuest && !user) && (
          <button
            data-testid="profile-signin-btn"
            onClick={() => navigate("/auth")}
            className="mt-4 w-full bg-white text-emerald-900 font-bold py-2.5 rounded-xl text-sm"
          >
            Sign in / Create Account
          </button>
        )}
      </div>

      {/* Quick links */}
      <div className="px-4 mt-5 grid grid-cols-4 gap-2">
        {[
          { icon: ShoppingBag, label: "Shop", path: "/products" },
          { icon: Salad, label: "Recipes", path: "/recipes" },
          { icon: CalendarCheck, label: "Consult", path: "/booking" },
          { icon: Leaf, label: "Guides", path: "/guide" },
        ].map((q) => (
          <button
            key={q.label}
            onClick={() => navigate(q.path)}
            className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800"
          >
            <q.icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">{q.label}</span>
          </button>
        ))}
      </div>

      {/* Settings */}
      <div className="px-5 mt-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 ml-1">Preferences</h3>
        <div className="rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              {dark ? <Moon className="w-5 h-5 text-emerald-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Dark Mode</span>
            </div>
            <button
              data-testid="dark-mode-toggle"
              onClick={toggleDark}
              className={`w-12 h-7 rounded-full p-0.5 transition-colors ${dark ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <span className={`block bg-white rounded-full shadow transition-transform ${dark ? "translate-x-5" : ""}`} style={{ width: 22, height: 22 }} />
            </button>
          </div>
          <Divider />
          <Row icon={UserCircle} label="Dietary Preferences" onClick={() => toast("Dietary preferences", { description: "Set during consultation booking." })} />
          <Divider />
          <Row icon={CalendarCheck} label="My Consultations" onClick={() => toast("No saved consultations yet")} />
        </div>
      </div>

      {/* Account actions */}
      <div className="px-5 mt-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 ml-1">Account</h3>
        <div className="rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 overflow-hidden">
          {user && (
            <>
              <button data-testid="profile-logout-btn" onClick={handleLogout} className="w-full flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <LogOut className="w-5 h-5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Log Out</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <Divider />
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="account-delete-btn"
                disabled={!user}
                className="w-full flex items-center justify-between px-4 py-3.5 disabled:opacity-40"
              >
                <div className="flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-rose-500" />
                  <span className="text-sm font-medium text-rose-600 dark:text-rose-400">Delete Account</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-[20rem] rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes your account and all associated data. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-row gap-2">
                <AlertDialogCancel className="flex-1 mt-0" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-confirm-btn"
                  onClick={handleDelete}
                  className="flex-1 bg-rose-600 hover:bg-rose-700"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        {!user && <p className="text-[11px] text-slate-400 mt-2 ml-1">Sign in to manage account actions.</p>}
      </div>

      <div className="mt-8 flex flex-col items-center gap-2">
        <BrandMark size={56} className="shadow-none border border-slate-100 dark:border-emerald-800" />
        <p className="text-center text-xs text-slate-400">U&URS NUTRITIONIST · v1.0.0</p>
      </div>
    </div>
  );
}

const Divider = () => <div className="h-px bg-slate-100 dark:bg-emerald-800 mx-4" />;

const Row = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center justify-between px-4 py-3.5">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-slate-500" />
      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-400" />
  </button>
);
